/**
 * Turning the API's `next_free_time` into a window staff can actually submit.
 *
 * The 409 slot-conflict body (api#81) says *when* the table frees up, but a
 * booking form needs a start **and** an end. This module owns that one step so
 * the arithmetic is unit-testable away from React, and so every surface that
 * offers the suggestion offers the same window.
 *
 * All maths is on minute-of-day via `time.ts`, deliberately: that gives one
 * parser for API time-of-day strings (rejecting "2:30 PM" and "12:75" alike)
 * and one spelling of the API's 24:00 end-of-day boundary.
 */
import { minutesOfDay, minutesToHm } from './time';

/** Minute-of-day for the API's exclusive end-of-day boundary ("24:00"). */
const END_OF_DAY = 24 * 60;

export interface SlotWindow {
  /** Canonical "HH:MM" — the form's `<TimeSelect>` value, not display text. */
  startTime: string;
  endTime: string;
}

/**
 * The window you get by moving the current one onto `nextFreeTime`, keeping
 * the duration staff already picked — or `null` when there is nothing sensible
 * to offer, in which case the caller must not show the action at all.
 *
 * Duration is preserved rather than reset because the length of a booking is a
 * real decision (an hour of table time was paid for or agreed on); only *when*
 * it happens is being renegotiated by the conflict.
 *
 * `null` covers every unusable case in one place, so callers stay a plain
 * truthiness check:
 *   - either end of the current window is missing or unparseable — there is no
 *     duration to preserve yet;
 *   - a non-positive duration (defensive: submit is already gated on it);
 *   - an unparseable or absent `nextFreeTime`;
 *   - a suggestion at or past 24:00, which leaves no room in the day.
 *
 * A suggestion that *would* run past midnight is clamped to 24:00 rather than
 * wrapping: the API rejects a wrapped window, and silently booking someone into
 * tomorrow is worse than handing back a shorter one they can see and adjust.
 */
export function windowAtNextFreeTime(
  startTime: string,
  endTime: string,
  nextFreeTime: string | null | undefined,
): SlotWindow | null {
  if (!nextFreeTime) return null;
  const start = minutesOfDay(startTime);
  const end = minutesOfDay(endTime);
  const next = minutesOfDay(nextFreeTime);
  if (start === null || end === null || next === null) return null;

  const duration = end - start;
  if (duration <= 0) return null;
  if (next >= END_OF_DAY) return null;

  return {
    startTime: minutesToHm(next),
    endTime: minutesToHm(Math.min(next + duration, END_OF_DAY)),
  };
}
